/**
 * Toast Component - Sistema de notificações reutilizável
 *
 * Uso:
 *   Toast.success('Título', 'Descrição opcional');
 *   Toast.error('Título', 'Descrição opcional');
 *   Toast.warning('Título', 'Descrição opcional');
 *   Toast.info('Título', 'Descrição opcional');
 *
 * Opções:
 *   Toast.success('Título', 'Descrição', { duration: 5000, closable: true });
 */

// Container dos toasts
let toastContainer = null;

// Configurações padrão
const defaultOptions = {
  duration: 5000,
  closable: true,
  position: 'top-right'
};

// Configurações de cada tipo de toast
const toastTypes = {
  success: {
    iconBg: '#22c55e',
    icon: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="white" stroke-width="2"/>
      <path d="M8 12L11 15L16 9" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`
  },
  warning: {
    iconBg: '#f59e0b',
    icon: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M12 9V13M12 17H12.01" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M10.29 3.86L1.82 18C1.64 18.3 1.55 18.64 1.55 19C1.55 19.36 1.64 19.7 1.82 20C2 20.3 2.26 20.56 2.56 20.74C2.86 20.92 3.21 21.01 3.56 21H20.44C20.79 21.01 21.14 20.92 21.44 20.74C21.74 20.56 22 20.3 22.18 20C22.36 19.7 22.45 19.36 22.45 19C22.45 18.64 22.36 18.3 22.18 18L13.71 3.86C13.53 3.56 13.27 3.32 12.97 3.15C12.67 2.98 12.34 2.89 12 2.89C11.66 2.89 11.33 2.98 11.03 3.15C10.73 3.32 10.47 3.56 10.29 3.86Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`
  },
  info: {
    iconBg: '#3b82f6',
    icon: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="white" stroke-width="2"/>
      <path d="M12 16V12M12 8H12.01" stroke="white" stroke-width="2" stroke-linecap="round"/>
    </svg>`
  },
  error: {
    iconBg: '#ef4444',
    icon: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="white" stroke-width="2"/>
      <path d="M15 9L9 15M9 9L15 15" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`
  }
};

/**
 * Cria o container dos toasts se não existir
 */
function ensureContainer(position = 'top-right') {
  if (toastContainer && document.body.contains(toastContainer)) {
    return toastContainer;
  }

  toastContainer = document.createElement('div');
  toastContainer.id = 'toast-container';

  const positionStyles = {
    'top-right': { top: '20px', right: '20px' },
    'top-left': { top: '20px', left: '20px' },
    'bottom-right': { bottom: '20px', right: '20px' },
    'bottom-left': { bottom: '20px', left: '20px' }
  };

  const pos = positionStyles[position] || positionStyles['top-right'];

  Object.assign(toastContainer.style, {
    position: 'fixed',
    ...pos,
    zIndex: '99999',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    pointerEvents: 'none'
  });

  document.body.appendChild(toastContainer);
  return toastContainer;
}

/**
 * Cria e exibe um toast
 */
function createToast(type, title, description = '', options = {}) {
  const opts = { ...defaultOptions, ...options };
  const typeConfig = toastTypes[type] || toastTypes.info;

  const container = ensureContainer(opts.position);

  // Toast element
  const toastEl = document.createElement('div');
  Object.assign(toastEl.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '14px 18px',
    borderRadius: '24px',
    backgroundColor: '#f5f5f5',
    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.15), 0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 10px rgba(0, 0, 0, 0.08)',
    pointerEvents: 'auto',
    transform: 'translateX(120%)',
    transition: 'transform 0.35s cubic-bezier(0.21, 1.02, 0.73, 1), opacity 0.3s ease',
    minWidth: '300px',
    maxWidth: '400px',
  });

  // Ícone container (círculo colorido)
  const iconContainer = document.createElement('div');
  Object.assign(iconContainer.style, {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: typeConfig.iconBg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: '0'
  });
  iconContainer.innerHTML = typeConfig.icon;

  // Texto container
  const textContainer = document.createElement('div');
  Object.assign(textContainer.style, {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  });

  // Título
  const titleEl = document.createElement('div');
  Object.assign(titleEl.style, {
    fontSize: '15px',
    fontWeight: '500',
    color: '#1a1a1a',
    lineHeight: '1.3'
  });
  titleEl.textContent = title;
  textContainer.appendChild(titleEl);

  // Descrição (se houver)
  if (description) {
    const descEl = document.createElement('div');
    Object.assign(descEl.style, {
      fontSize: '13px',
      fontWeight: '400',
      color: '#6b7280',
      lineHeight: '1.4'
    });
    descEl.textContent = description;
    textContainer.appendChild(descEl);
  }

  // Monta o toast
  toastEl.appendChild(iconContainer);
  toastEl.appendChild(textContainer);

  // Botão fechar
  if (opts.closable) {
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Fechar');
    Object.assign(closeBtn.style, {
      width: '28px',
      height: '28px',
      borderRadius: '8px',
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s',
      flexShrink: '0',
      marginLeft: '4px'
    });
    closeBtn.innerHTML = `
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="#9ca3af" stroke-width="1.5" stroke-linecap="round"/>
      </svg>
    `;
    closeBtn.onmouseenter = () => { closeBtn.style.backgroundColor = 'rgba(0,0,0,0.05)'; };
    closeBtn.onmouseleave = () => { closeBtn.style.backgroundColor = 'transparent'; };
    closeBtn.onclick = removeToast;
    toastEl.appendChild(closeBtn);
  }

  container.appendChild(toastEl);

  let autoCloseTimer = null;

  function removeToast() {
    if (autoCloseTimer) clearTimeout(autoCloseTimer);
    toastEl.style.transform = 'translateX(120%)';
    toastEl.style.opacity = '0';

    setTimeout(() => {
      if (toastEl.parentNode) {
        toastEl.parentNode.removeChild(toastEl);
      }
      if (container.children.length === 0 && container.parentNode) {
        container.parentNode.removeChild(container);
        toastContainer = null;
      }
    }, 350);
  }

  // Animação de entrada
  setTimeout(() => {
    toastEl.style.transform = 'translateX(0)';
  }, 10);

  // Auto-fechar
  if (opts.duration > 0) {
    autoCloseTimer = setTimeout(removeToast, opts.duration);
  }

  // Pausa ao hover
  toastEl.addEventListener('mouseenter', () => {
    if (autoCloseTimer) {
      clearTimeout(autoCloseTimer);
      autoCloseTimer = null;
    }
  });

  toastEl.addEventListener('mouseleave', () => {
    if (opts.duration > 0 && !autoCloseTimer) {
      autoCloseTimer = setTimeout(removeToast, opts.duration);
    }
  });

  return { close: removeToast };
}

/**
 * API pública do toast
 */
export const toast = {
  success(title, description = '', options = {}) {
    if (typeof description === 'object') {
      options = description;
      description = '';
    }
    return createToast('success', title, description, options);
  },

  error(title, description = '', options = {}) {
    if (typeof description === 'object') {
      options = description;
      description = '';
    }
    return createToast('error', title, description, options);
  },

  warning(title, description = '', options = {}) {
    if (typeof description === 'object') {
      options = description;
      description = '';
    }
    return createToast('warning', title, description, options);
  },

  info(title, description = '', options = {}) {
    if (typeof description === 'object') {
      options = description;
      description = '';
    }
    return createToast('info', title, description, options);
  },

  show(type, title, description = '', options = {}) {
    return createToast(type, title, description, options);
  }
};

export default toast;

if (typeof window !== 'undefined') {
  window.Toast = toast;
}
